import React, { useState } from "react";
import { Lock, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

const fmt = (a, c) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(a || 0);

/**
 * Paywall card shown below the article preview when the user has not paid.
 * Triggers a Stripe Checkout session for either the article or the parent magazine.
 */
export default function Paywall({ article, magazineId, magazinePrice, magazineCurrency = "EUR" }) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const buy = async (kind, slugOrId) => {
    setErr("");
    setBusy(true);
    try {
      const origin_url = window.location.origin;
      const path = kind === "article"
        ? `/checkout/article/${slugOrId}`
        : `/checkout/magazine/${slugOrId}`;
      const { data } = await api.post(path, { origin_url, email: email || undefined });
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur paiement");
      setBusy(false);
    }
  };

  return (
    <div className="my-12 relative" data-testid="paywall">
      {/* Fade-out gradient over the preview text */}
      <div
        className="absolute -top-32 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, var(--gai-white) 92%)",
        }}
      />
      <div className="border" style={{ borderColor: "var(--gai-red)", background: "var(--gai-grey-50)" }}>
        <div className="p-8 md:p-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6"
               style={{ background: "var(--gai-black)", color: "var(--gai-red)" }}>
            <Lock size={14} />
            <span className="text-[0.7rem] tracking-[0.22em] uppercase font-semibold">Article premium</span>
          </div>
          <h3 className="serif text-3xl md:text-4xl" style={{ color: "var(--gai-black)" }}>
            Continuez votre lecture.
          </h3>
          <p className="mt-3 max-w-xl mx-auto text-[rgba(5,16,36,0.65)] leading-relaxed">
            Cet article fait partie de notre offre éditoriale premium. Achetez-le à l'unité, ou accédez à l'édition complète du magazine.
          </p>

          <div className="mt-8 max-w-sm mx-auto">
            <input
              type="email"
              placeholder="Votre email (pour recevoir l'accès)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border bg-white outline-none focus:border-[var(--gai-red)]"
              style={{ borderColor: "rgba(5,16,36,0.2)", color: "var(--gai-black)" }}
              data-testid="paywall-email"
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => buy("article", article.slug)}
              disabled={busy}
              className="gai-btn-gold"
              data-testid="paywall-buy-article"
            >
              <ShoppingBag size={14} /> Acheter cet article — {fmt(article.price || 4, article.currency || "EUR")}
            </button>
            {magazineId && magazinePrice > 0 && (
              <button
                onClick={() => buy("magazine", magazineId)}
                disabled={busy}
                className="gai-btn-outline"
                style={{ color: "var(--gai-black)", borderColor: "var(--gai-black)" }}
                data-testid="paywall-buy-magazine"
              >
                Magazine complet — {fmt(magazinePrice, magazineCurrency)}
              </button>
            )}
          </div>

          {err && <p className="mt-4 text-sm text-red-500" data-testid="paywall-err">{err}</p>}

          <p className="mt-6 text-[0.7rem] tracking-[0.18em] uppercase" style={{ color: "rgba(5,16,36,0.5)" }}>
            Paiement sécurisé · Stripe · Accès immédiat
          </p>
        </div>
      </div>

      <div className="mt-6 text-center text-sm">
        <button onClick={() => navigate("/annoncer")} className="text-[var(--gai-black)] underline" style={{ textDecorationColor: "var(--gai-red)" }}>
          Vous êtes une entreprise ? Apparaissez dans notre prochain numéro →
        </button>
      </div>
    </div>
  );
}
